import {
	type AttemptsResultDetail,
	type BoutMeetConfig,
	type BoutResultDetail,
	RESULT_VALUE_DECIMALS,
	type RoundsMeetConfig,
	type RoundsResultDetail,
	type SetsMeetConfig,
	type SetsResultDetail,
} from "./results";
import { cricketVerdict, hasInnings } from "./cricket";
import { type SetRule, setRuleOf } from "./set-rules";
import type { IrmCode, MeetOutcome } from "./vocab";

/**
 * Ranking rules — ONE implementation of "who is ahead", shared by OVR (the
 * live rank and outcome an operator sees while typing) and, later, the API.
 *
 * Why it lives here: the predecessor computed rank in each of its ~125 scoring
 * forms, behind a "Calculate" button, with a hand-ticked winner checkbox that
 * could contradict the score; 97 commits went into fixing ranks. Here every
 * rule is a pure function with a unit test (`apps/api/tests/unit/ranking.test.ts`).
 *
 * Conventions shared with phase standings (`apps/api/src/db/queries/standings.queries.ts`):
 *  · competition ranking — ties share a rank and the next rank is skipped (1, 1, 3);
 *  · a row with an invalid-result mark (IRM) or no value gets NO rank;
 *  · unranked rows are listed after the ranked ones: DNF / NM, then DNS, then
 *    the rest (`unrankedOrder`, the same CASE as `unrankedOrderSql`).
 *
 * Head-to-head helpers return a `HeadToHeadVerdict` for a two-slot meet: the
 * two headline numbers (`score` — what each slot's `result_value` should be:
 * sets / games / rubbers won, goals) and the two outcomes, or `null` while the
 * meet is not decided. An IRM is always carried by the side that has no valid
 * result (the absent, retired or disqualified one), so it loses.
 *
 * Everything here is arithmetic on canonical units (ms, mm, points as
 * counted); sums are rounded to `RESULT_VALUE_DECIMALS` so they pass
 * `resultValueField` (0.1 + 0.2 is not 0.3 in a double).
 */

// ─── Shared ──────────────────────────────────────────────────────────────────

/** Rounds a sum to what `numeric(14, 4)` stores, so float noise never reaches a save. */
const roundValue = (value: number): number => Number(value.toFixed(RESULT_VALUE_DECIMALS));

const isNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

/** Two outcomes for a two-slot meet, index-aligned with the slots given. */
export type HeadToHeadOutcomes = readonly [MeetOutcome, MeetOutcome];

export interface HeadToHeadVerdict {
	/** Each slot's headline number (`result_value`): sets, games, rubbers or goals. */
	score: readonly [number, number];
	/** null while the meet is not decided (not enough sets, a level score, conflicting data). */
	outcomes: HeadToHeadOutcomes | null;
}

/** One side of a head-to-head meet: its family `detail` and its IRM. */
export interface HeadToHeadSide<D> {
	detail: D | null | undefined;
	irm: IrmCode | null;
}

const WIN_LOSS: HeadToHeadOutcomes = ["WIN", "LOSS"];
const LOSS_WIN: HeadToHeadOutcomes = ["LOSS", "WIN"];
const DRAW_DRAW: HeadToHeadOutcomes = ["DRAW", "DRAW"];

/** The winner's index (0 / 1) as outcomes. */
const outcomesFor = (winner: 0 | 1): HeadToHeadOutcomes => (winner === 0 ? WIN_LOSS : LOSS_WIN);

/**
 * The IRM rule for every head-to-head family: exactly one side has a mark →
 * the other side wins (walkover, retirement, disqualification). Both marked →
 * undecided (a double forfeit is the referee's call, entered by hand).
 * `undefined` = no IRM involved, decide on the score.
 */
function irmVerdict(a: IrmCode | null, b: IrmCode | null): HeadToHeadOutcomes | null | undefined {
	if (a === null && b === null) return undefined;
	if (a !== null && b !== null) return null;
	return outcomesFor(a === null ? 0 : 1);
}

// ─── Rank by value ───────────────────────────────────────────────────────────

export interface RankableRow<Id> {
	id: Id;
	/** The canonical value (`result_value`): ms, mm, points. null = no result yet. */
	value: number | null;
	irm: IrmCode | null;
	/**
	 * Compared element by element when two values are equal — SMALLER WINS,
	 * whatever the direction of `value`. A missing element counts as worse
	 * than any number (a countback mark the competitor does not have).
	 */
	tieBreak?: readonly number[] | undefined;
}

function compareTieBreak(a: readonly number[] | undefined, b: readonly number[] | undefined): number {
	const left = a ?? [];
	const right = b ?? [];
	const length = Math.max(left.length, right.length);
	for (let i = 0; i < length; i++) {
		const x = left[i] ?? Number.POSITIVE_INFINITY;
		const y = right[i] ?? Number.POSITIVE_INFINITY;
		if (x !== y) return x < y ? -1 : 1;
	}
	return 0;
}

/**
 * Competition ranking ("1224" / standard competition ranking) of a meet's rows
 * by their value.
 *
 * Rule: better value first (`lowerIsBetter` for times and strokes, higher for
 * distances and points); equal values are compared on `tieBreak`
 * lexicographically, smaller first; rows still equal share a rank and the next
 * rank skips (1, 1, 3). A row with an IRM, or without a finite value, gets
 * `null` — a disqualified swimmer with a time on the board is NOT ranked (the
 * predecessor ranked DSQ rows that had a time).
 *
 * Source: the standings convention (`rank()` in `standings.queries.ts`);
 * World Athletics Technical Rule 21 (ties), World Aquatics SW 11 (timing).
 * A tie that the rules break by a jump-off or a swim-off is NOT resolved here:
 * the result of the decider is a decision, entered as the phase's `tie_order`.
 *
 * @returns a rank for every id given (null = unranked).
 */
export function rankByValue<Id>(rows: readonly RankableRow<Id>[], lowerIsBetter: boolean): Map<Id, number | null> {
	const ranks = new Map<Id, number | null>();
	const ranked: RankableRow<Id>[] = [];
	for (const row of rows) {
		if (row.irm === null && isNumber(row.value)) ranked.push(row);
		else ranks.set(row.id, null);
	}
	const compare = (a: RankableRow<Id>, b: RankableRow<Id>): number => {
		const av = a.value as number;
		const bv = b.value as number;
		if (av !== bv) return (av < bv ? -1 : 1) * (lowerIsBetter ? 1 : -1);
		return compareTieBreak(a.tieBreak, b.tieBreak);
	};
	ranked.sort(compare);
	ranked.forEach((row, i) => {
		const previous = ranked[i - 1];
		ranks.set(row.id, previous !== undefined && compare(previous, row) === 0 ? (ranks.get(previous.id) as number) : i + 1);
	});
	return ranks;
}

/**
 * Where an UNRANKED row goes among the unranked: DNF / NM (they competed),
 * then DNS, then every other mark and rows with no result yet. The same
 * CASE as `unrankedOrderSql` in the standings query.
 */
export function unrankedOrder(irm: IrmCode | null): 1 | 2 | 3 {
	if (irm === "DNF" || irm === "NM") return 1;
	if (irm === "DNS") return 2;
	return 3;
}

/**
 * A meet's rows in result order: ranked rows by rank, then the unranked by
 * `unrankedOrder`; equal keys keep the order given (stable). For read-only
 * views — an entry grid must not reorder rows under the operator's cursor.
 */
export function orderByRank<Row extends { id: Id; irm: IrmCode | null }, Id>(rows: readonly Row[], ranks: ReadonlyMap<Id, number | null>): Row[] {
	const key = (row: Row): [number, number] => {
		const rank = ranks.get(row.id) ?? null;
		return rank === null ? [1, unrankedOrder(row.irm)] : [0, rank];
	};
	return rows
		.map((row, index) => ({ row, index, key: key(row) }))
		.sort((a, b) => a.key[0] - b.key[0] || a.key[1] - b.key[1] || a.index - b.index)
		.map((x) => x.row);
}

// ─── ATTEMPTS — horizontal jumps and throws ──────────────────────────────────

export type Attempt = NonNullable<AttemptsResultDetail["attempts"]>[number];
export type HeightTries = NonNullable<AttemptsResultDetail["heights"]>[number];

/** The valid marks of a series, best (longest) first. A VALID attempt without a mark does not count. */
function validMarks(attempts: readonly Attempt[] | null | undefined): Attempt[] {
	return (attempts ?? [])
		.filter((a) => a.result === "VALID" && isNumber(a.mark) && a.mark >= 0)
		.sort((a, b) => (b.mark as number) - (a.mark as number) || a.seq - b.seq);
}

/**
 * The best valid mark of a jump / throw series and the attempt it came from
 * (`result_value` and `best_seq`). Higher is better: ATTEMPTS marks are
 * distances in millimetres. FOUL and PASS never count. Equal best marks in two
 * attempts → the earlier attempt is `seq` (the mark was first reached there).
 *
 * Source: World Athletics Technical Rules 25.20 / 25.21 (each athlete is
 * credited with the best of all their trials). `null` = no valid mark (NM).
 */
export function bestAttempt(attempts: readonly Attempt[] | null | undefined): { mark: number; seq: number } | null {
	const best = validMarks(attempts)[0];
	return best === undefined ? null : { mark: best.mark as number, seq: best.seq };
}

/**
 * Countback for a horizontal jump / throw tie, as a `tieBreak` for
 * `rankByValue`: the second-best valid mark, then the third-best, and so on,
 * negated (so SMALLER wins, as `rankByValue` expects). Fewer valid marks =
 * shorter array = worse, because a missing element loses to any number.
 * Equal all the way down → the tie stands (shared rank).
 *
 * Source: World Athletics Technical Rules 25.22 ("the second best performance
 * of the athletes tied shall determine whether there is a tie, then if
 * necessary the third best, and so on").
 */
export function attemptsTieBreak(attempts: readonly Attempt[] | null | undefined): number[] {
	return validMarks(attempts)
		.slice(1)
		.map((a) => -(a.mark as number));
}

/**
 * The best cleared height of a high jump / pole vault card and its countback.
 *
 *   height     the highest bar with an `O` in its tries
 *   tieBreak   [failures at that height, total failures up to and including it]
 *              — smaller wins, the `rankByValue` convention
 *
 * Failures are `X`; a pass (`-`) is not a failure. Failures at heights ABOVE
 * the best cleared height do not count. `null` = nothing cleared (NM).
 *
 * Source: World Athletics Technical Rule 26.8.1 (fewest jumps at the height
 * last cleared, then fewest failures throughout the competition up to and
 * including that height). A tie for first that remains is decided by a
 * jump-off (TR 26.9) — a decision, entered as the phase's `tie_order`.
 */
export function bestHeight(heights: readonly HeightTries[] | null | undefined): { height: number; tieBreak: [number, number] } | null {
	const card = [...(heights ?? [])].filter((h) => isNumber(h.height)).sort((a, b) => a.height - b.height);
	let best: { height: number; tieBreak: [number, number] } | null = null;
	let failures = 0;
	for (const h of card) {
		const misses = [...h.tries].filter((c) => c === "X").length;
		failures += misses;
		if (h.tries.includes("O")) best = { height: h.height, tieBreak: [misses, failures] };
	}
	return best;
}

/** What an ATTEMPTS row ranks by. */
export interface AttemptsSummary {
	/** `result_value` — best mark or best cleared height, mm. null = NM. */
	value: number | null;
	/** `best_seq` — the attempt the best mark came from (null for a heights card). */
	bestSeq: number | null;
	/** Countback, for `rankByValue`. */
	tieBreak: number[];
}

/**
 * One call for an ATTEMPTS row: a heights card (high jump, pole vault) when
 * `heights` holds anything, otherwise the attempts series (jumps, throws).
 */
export function attemptsSummary(detail: AttemptsResultDetail | null | undefined): AttemptsSummary {
	if (detail?.heights !== undefined && detail.heights.length > 0) {
		const best = bestHeight(detail.heights);
		return { value: best?.height ?? null, bestSeq: null, tieBreak: best?.tieBreak ?? [] };
	}
	const best = bestAttempt(detail?.attempts);
	return { value: best?.mark ?? null, bestSeq: best?.seq ?? null, tieBreak: attemptsTieBreak(detail?.attempts) };
}

// ─── SETS — racket, net and period sports ────────────────────────────────────

export { SET_RULE_CODES, SET_RULE_PRESETS, TIEBREAK_POINTS_MAX, type SetRule, type SetRuleCode, setRuleOf } from "./set-rules";

export type SetState = "IN_PROGRESS" | "COMPLETE" | "INVALID";

/**
 * Whether a set score is finished, still being played, or impossible under
 * the rule. `21–20` in badminton is IN_PROGRESS (the predecessor counted it as
 * a won game), `23–19` is INVALID (the game ended at 21–19), `30–29` COMPLETE.
 */
export function setState(a: number, b: number, rule: SetRule, deciding = false): SetState {
	if (!Number.isInteger(a) || !Number.isInteger(b) || a < 0 || b < 0) return "INVALID";
	const r = deciding && rule.decider !== undefined ? rule.decider : rule;
	const hi = Math.max(a, b);
	const lo = Math.min(a, b);
	const gap = hi - lo;
	if (r.cap !== undefined && hi > r.cap) return "INVALID";
	if (hi < r.points) return "IN_PROGRESS";
	if (r.cap !== undefined && hi === r.cap && r.cap > r.points) {
		// At the cap, one point clear is enough; a lead larger than winBy could not have got here.
		if (gap === 0) return "INVALID";
		return gap <= r.winBy ? "COMPLETE" : "INVALID";
	}
	if (hi === r.points) return gap >= r.winBy ? "COMPLETE" : "IN_PROGRESS";
	// Past the target the set ends the moment the lead reaches winBy.
	if (gap === r.winBy) return "COMPLETE";
	return gap < r.winBy ? "IN_PROGRESS" : "INVALID";
}

/** The two sides' periods paired by `seq`, in `seq` order. */
function pairedPeriods(a: SetsResultDetail | null | undefined, b: SetsResultDetail | null | undefined) {
	const seqs = new Set<number>();
	for (const p of a?.periods ?? []) seqs.add(p.seq);
	for (const p of b?.periods ?? []) seqs.add(p.seq);
	return [...seqs]
		.sort((x, y) => x - y)
		.map((seq) => ({
			seq,
			a: a?.periods?.find((p) => p.seq === seq),
			b: b?.periods?.find((p) => p.seq === seq),
		}));
}

export interface SetsCount {
	/** Sets won by each side. */
	won: [number, number];
	/** Sets whose score is impossible under the rule — the screen warns on them. */
	invalidSeqs: number[];
}

/**
 * Sets won by each side of a best-of match (badminton, table tennis,
 * volleyball, tennis).
 *
 * A set counts for the side with the higher score; on equal scores the higher
 * `tiebreak` (tennis 7–6 (7–4)); otherwise it counts for nobody. With a
 * `rule`, only COMPLETE sets count, and impossible ones are reported in
 * `invalidSeqs`; without one, any lead counts — so give a rule wherever a
 * preset exists, or a set in progress already looks won. A period missing on
 * one side is not counted.
 *
 * Source: BWF Law 7, ITTF 2.11 / 2.12, FIVB 6.2, ITF Rules 5–6 (a match is won
 * by the majority of its games / sets).
 */
export function setsWon(a: SetsResultDetail | null | undefined, b: SetsResultDetail | null | undefined, rule?: SetRule, bestOf?: number): SetsCount {
	const won: [number, number] = [0, 0];
	const invalidSeqs: number[] = [];
	for (const p of pairedPeriods(a, b)) {
		if (p.a === undefined || p.b === undefined) continue;
		if (rule !== undefined) {
			const state = setState(p.a.score, p.b.score, rule, bestOf !== undefined && p.seq === bestOf);
			if (state === "INVALID") invalidSeqs.push(p.seq);
			if (state !== "COMPLETE") continue;
		}
		if (p.a.score !== p.b.score) won[p.a.score > p.b.score ? 0 : 1]++;
		else if (isNumber(p.a.tiebreak) && isNumber(p.b.tiebreak) && p.a.tiebreak !== p.b.tiebreak) won[p.a.tiebreak > p.b.tiebreak ? 0 : 1]++;
	}
	return { won, invalidSeqs };
}

/** A side's total in a period sport: every period, plus extra time. The shoot-out is not part of it. */
export function periodTotal(detail: SetsResultDetail | null | undefined): number {
	const periods = (detail?.periods ?? []).reduce((sum, p) => sum + p.score, 0);
	const extra = (detail?.extra ?? []).reduce((sum, p) => sum + p.score, 0);
	return periods + extra;
}

export interface SetsVerdict extends HeadToHeadVerdict {
	/** Impossible set scores (best-of with a rule only). */
	invalidSeqs: number[];
}

/**
 * WIN / LOSS / DRAW for a two-slot SETS meet, from the scores — never a
 * ticked box.
 *
 *  · IRM on one side → the other side wins (W.O., RET, DSQ), whatever the score.
 *  · `config.best_of` set → a best-of match: `score` is sets won; the first to
 *    the majority (`floor(best_of / 2) + 1`) wins; nobody there yet → null.
 *    Both there (impossible data) → null. No draw.
 *  · no `best_of` → a period sport (football, hockey, basketball): `score` is
 *    the total of periods + extra time; the higher total wins; equal totals →
 *    the shoot-out decides when both sides have one and they differ; equal
 *    totals with no shoot-out on either side → DRAW (a group match); a
 *    shoot-out on one side only, or level → null (being entered).
 *
 * Whether a period-sport meet is finished is the operator's call (the status
 * stepper): the verdict says who leads now.
 *
 * Source: the majority rule as in `setsWon`; IFAB Laws of the Game 10 (the
 * team scoring more goals wins; equal = draw; penalties only to decide a
 * knockout match and not part of the score).
 */
export function setsWinner(
	sides: readonly [HeadToHeadSide<SetsResultDetail>, HeadToHeadSide<SetsResultDetail>],
	config: SetsMeetConfig | null | undefined,
	rule?: SetRule,
): SetsVerdict {
	const [a, b] = sides;
	// Cricket records innings, not periods: its own rule (runs, then the super over).
	if (hasInnings(a.detail, b.detail)) {
		const v = cricketVerdict(sides, config?.overs);
		return { score: v.score, outcomes: v.outcomes, invalidSeqs: [] };
	}
	// The meet's own rule (a tennis doubles rubber's match tie-break) wins over the sport's.
	const setRule = setRuleOf(config?.rule) ?? rule;
	const raceTo = config?.race_to;
	if (raceTo !== undefined) {
		// Pool: racks won are the periods' sum (typed rack by rack, or as one total); the first to race_to wins.
		const won: [number, number] = [periodTotal(a.detail), periodTotal(b.detail)];
		const byIrm = irmVerdict(a.irm, b.irm);
		if (byIrm !== undefined) return { score: won, outcomes: byIrm, invalidSeqs: [] };
		const aDone = won[0] >= raceTo;
		const bDone = won[1] >= raceTo;
		return { score: won, outcomes: aDone === bDone ? null : outcomesFor(aDone ? 0 : 1), invalidSeqs: [] };
	}
	const bestOf = config?.best_of;
	if (bestOf !== undefined) {
		const { won, invalidSeqs } = setsWon(a.detail, b.detail, setRule, bestOf);
		const byIrm = irmVerdict(a.irm, b.irm);
		if (byIrm !== undefined) return { score: won, outcomes: byIrm, invalidSeqs };
		if (config?.play_all === true) {
			// Every game is played: decided once all are, a level count is a draw.
			const played = pairedPeriods(a.detail, b.detail).filter((p) => p.a !== undefined && p.b !== undefined).length;
			if (played < bestOf) return { score: won, outcomes: null, invalidSeqs };
			return { score: won, outcomes: won[0] === won[1] ? DRAW_DRAW : outcomesFor(won[0] > won[1] ? 0 : 1), invalidSeqs };
		}
		const majority = Math.floor(bestOf / 2) + 1;
		const aDone = won[0] >= majority;
		const bDone = won[1] >= majority;
		const outcomes = aDone === bDone ? null : outcomesFor(aDone ? 0 : 1);
		return { score: won, outcomes, invalidSeqs };
	}
	const score: [number, number] = [periodTotal(a.detail), periodTotal(b.detail)];
	const byIrm = irmVerdict(a.irm, b.irm);
	if (byIrm !== undefined) return { score, outcomes: byIrm, invalidSeqs: [] };
	if (score[0] !== score[1]) return { score, outcomes: outcomesFor(score[0] > score[1] ? 0 : 1), invalidSeqs: [] };
	const sa = a.detail?.shootout;
	const sb = b.detail?.shootout;
	if (sa === undefined && sb === undefined) return { score, outcomes: DRAW_DRAW, invalidSeqs: [] };
	if (sa !== undefined && sb !== undefined && sa !== sb) return { score, outcomes: outcomesFor(sa > sb ? 0 : 1), invalidSeqs: [] };
	return { score, outcomes: null, invalidSeqs: [] };
}

// ─── BOUT — combat sports ────────────────────────────────────────────────────

/**
 * How a sport decides a bout on the score:
 *
 *   ROUNDS   round by round — a round goes to the corner whose round entry
 *            carries a `win_by` (a level round decided on tie-break, WT
 *            superiority), else to the higher round score; the first to the
 *            majority of `config.rounds` wins (taekwondo, WT Competition Rules:
 *            best of three rounds; wushu sanda, IWUF: best of three)
 *   JUDGES   judges' cards — each seat votes for the higher card (its `total`,
 *            else the sum of its `scores`); more votes wins (boxing, World
 *            Boxing: 10-point must system; muay, IFMA)
 *   TOTAL    the sum of round scores — the higher total wins; with
 *            `config.target`, a corner that reaches the target wins at once
 *            (karate kumite, pencak silat tanding, fencing: 5 / 15 / 45)
 *   TALLIES  the score ladder — more ippon, then more waza-ari, then more
 *            yuko, then FEWER shido (judo, IJF; the old OVR's rule). `score`
 *            is the classic scoreboard figure: ippon × 100 + waza-ari × 10 +
 *            yuko ("100", "010")
 */
export const BOUT_DECISIONS = ["ROUNDS", "JUDGES", "TOTAL", "TALLIES"] as const;
export type BoutDecision = (typeof BOUT_DECISIONS)[number];

/** The tallies TALLIES reads, in ladder order (`detail.tallies[].code`), and the penalty it counts. */
export const BOUT_LADDER_TALLIES = ["ippon", "waza_ari", "yuko"] as const;
export const BOUT_LADDER_PENALTY = "shido";

export interface BoutRules {
	decideBy: BoutDecision;
	/** TOTAL only: a level total is a DRAW (a team-kumite bout) instead of undecided (golden score). */
	allowDraw?: boolean | undefined;
}

export interface BoutVerdict extends HeadToHeadVerdict {
	/**
	 * What decided it: an IRM, a `win_by` recorded on exactly one side (a
	 * stoppage the score cannot show — RSC, KO, DSQ), or the score.
	 */
	basis: "IRM" | "WIN_BY" | "SCORE" | null;
	/** What the score alone says — the cross-check for a `win_by` decision. */
	scoreOutcomes: HeadToHeadOutcomes | null;
	/** True when a `win_by` names a winner the score does not — the screen asks the operator to confirm. */
	conflict: boolean;
}

/**
 * Round scores as recorded. A round `score` is the points on the board,
 * INCLUDING points awarded for the opponent's penalties; `penalties` is a
 * count and is never subtracted (the predecessor took a gam-jeom off the
 * offender, where the WT Competition Rules give the opponent a point).
 */
function roundScore(detail: BoutResultDetail | null | undefined, seq: number): number | undefined {
	const score = detail?.rounds?.find((r) => r.seq === seq)?.score;
	return isNumber(score) ? score : undefined;
}

/**
 * A round's recorded decision (`rounds[].win_by`) that the score
 * contradicts: it sits on the corner with the LOWER round score. Only a
 * level round (superiority, a tie-break) or a round the score cannot show
 * (one score missing) may go to a corner its score does not name — a PTG
 * picked in the wrong row must never win the loser the round silently. The
 * screen shows it and asks; `boutRoundWinner` ignores such a decision.
 */
export function boutRoundConflict(a: BoutResultDetail | null | undefined, b: BoutResultDetail | null | undefined, seq: number): boolean {
	const aBy = a?.rounds?.find((r) => r.seq === seq)?.win_by !== undefined;
	const bBy = b?.rounds?.find((r) => r.seq === seq)?.win_by !== undefined;
	if (aBy === bBy) return false;
	const x = roundScore(a, seq);
	const y = roundScore(b, seq);
	if (x === undefined || y === undefined) return false;
	return aBy ? x < y : y < x;
}

/**
 * Who won round `seq`: the corner whose round entry carries a `win_by`
 * (exactly one — the round's decision, which also settles a level round)
 * unless the score contradicts it (`boutRoundConflict`: then the score
 * stands); else the higher score once both are entered; null when level,
 * unscored, or both carry one.
 */
export function boutRoundWinner(a: BoutResultDetail | null | undefined, b: BoutResultDetail | null | undefined, seq: number): 0 | 1 | null {
	const aBy = a?.rounds?.find((r) => r.seq === seq)?.win_by !== undefined;
	const bBy = b?.rounds?.find((r) => r.seq === seq)?.win_by !== undefined;
	if (aBy !== bBy && !boutRoundConflict(a, b, seq)) return aBy ? 0 : 1;
	if (aBy && bBy) return null;
	const x = roundScore(a, seq);
	const y = roundScore(b, seq);
	if (x === undefined || y === undefined || x === y) return null;
	return x > y ? 0 : 1;
}

/** A judge's card total: its `total`, else the sum of its round scores; null when it has neither. */
export function boutCardTotal(judge: { total?: number | undefined; scores?: number[] | undefined } | null | undefined): number | null {
	if (!judge) return null;
	if (isNumber(judge.total)) return judge.total;
	return judge.scores !== undefined && judge.scores.length > 0 ? roundValue(judge.scores.reduce((s, v) => s + v, 0)) : null;
}

/**
 * True once either corner holds what the decision counts — a judge's card
 * (JUDGES), a ladder tally (TALLIES), a round score (ROUNDS, TOTAL). Before
 * that, a bout has no score to show (never a made-up 0–0): OVR writes no
 * `result_display` and the public feed sends no `a` / `b`.
 */
export function boutHasScore(decideBy: BoutDecision, a: BoutResultDetail | null | undefined, b: BoutResultDetail | null | undefined): boolean {
	if (decideBy === "JUDGES") return [a, b].some((d) => (d?.judges ?? []).some((j) => boutCardTotal(j) !== null));
	if (decideBy === "TALLIES") return anyLadder(a) || anyLadder(b);
	return [a, b].some((d) => (d?.rounds ?? []).some((r) => isNumber(r.score)));
}

/** A tally's value on a slot (`detail.tallies`), 0 when not recorded. */
export function boutTally(detail: BoutResultDetail | null | undefined, code: string): number {
	const v = detail?.tallies?.find((t) => t.code === code)?.value;
	return isNumber(v) ? v : 0;
}

/** TALLIES' shido count: the `shido` tally, else the slot's penalty count, else its rounds' penalties summed. */
function ladderPenalty(detail: BoutResultDetail | null | undefined): number {
	if (detail?.tallies?.some((t) => t.code === BOUT_LADDER_PENALTY)) return boutTally(detail, BOUT_LADDER_PENALTY);
	if (isNumber(detail?.penalties)) return detail.penalties;
	return (detail?.rounds ?? []).reduce((s, r) => s + (isNumber(r.penalties) ? r.penalties : 0), 0);
}

/** The TALLIES scoreboard figure: ippon × 100 + waza-ari × 10 + yuko. */
export function ladderFigure(detail: BoutResultDetail | null | undefined): number {
	const [i, w, y] = BOUT_LADDER_TALLIES.map((c) => boutTally(detail, c)) as [number, number, number];
	return i * 100 + w * 10 + y;
}

/** True once the slot records anything the TALLIES ladder reads. */
const anyLadder = (d: BoutResultDetail | null | undefined) => (d?.tallies ?? []).some((t) => (BOUT_LADDER_TALLIES as readonly string[]).includes(t.code) || t.code === BOUT_LADDER_PENALTY);

function boutScore(
	a: BoutResultDetail | null | undefined,
	b: BoutResultDetail | null | undefined,
	rules: BoutRules,
	config: BoutMeetConfig | null | undefined,
): { score: [number, number]; outcomes: HeadToHeadOutcomes | null } {
	if (rules.decideBy === "ROUNDS") {
		const seqs = [...new Set([...(a?.rounds ?? []), ...(b?.rounds ?? [])].map((r) => r.seq))];
		const won: [number, number] = [0, 0];
		for (const seq of seqs) {
			const w = boutRoundWinner(a, b, seq);
			if (w !== null) won[w]++;
		}
		if (config?.rounds === undefined) {
			// Format unknown: more rounds won leads.
			return { score: won, outcomes: won[0] === won[1] ? null : outcomesFor(won[0] > won[1] ? 0 : 1) };
		}
		const majority = Math.floor(config.rounds / 2) + 1;
		const aDone = won[0] >= majority;
		const bDone = won[1] >= majority;
		return { score: won, outcomes: aDone === bDone ? null : outcomesFor(aDone ? 0 : 1) };
	}
	if (rules.decideBy === "JUDGES") {
		const card = (d: BoutResultDetail | null | undefined, seat: string): number | undefined => boutCardTotal(d?.judges?.find((x) => x.seat === seat)) ?? undefined;
		const seats = [...new Set([...(a?.judges ?? []), ...(b?.judges ?? [])].map((j) => j.seat))];
		const votes: [number, number] = [0, 0];
		for (const seat of seats) {
			const x = card(a, seat);
			const y = card(b, seat);
			if (x !== undefined && y !== undefined && x !== y) votes[x > y ? 0 : 1]++;
		}
		return { score: votes, outcomes: votes[0] === votes[1] ? null : outcomesFor(votes[0] > votes[1] ? 0 : 1) };
	}
	if (rules.decideBy === "TALLIES") {
		const score: [number, number] = [ladderFigure(a), ladderFigure(b)];
		if (!anyLadder(a) && !anyLadder(b)) return { score, outcomes: null };
		for (const c of BOUT_LADDER_TALLIES) {
			const x = boutTally(a, c);
			const y = boutTally(b, c);
			if (x !== y) return { score, outcomes: outcomesFor(x > y ? 0 : 1) };
		}
		const pa = ladderPenalty(a);
		const pb = ladderPenalty(b);
		if (pa !== pb) return { score, outcomes: outcomesFor(pa < pb ? 0 : 1) };
		return { score, outcomes: null }; // level: golden score
	}
	const total = (d: BoutResultDetail | null | undefined) => roundValue((d?.rounds ?? []).reduce((s, r) => s + (isNumber(r.score) ? r.score : 0), 0));
	const score: [number, number] = [total(a), total(b)];
	const target = config?.target;
	if (target !== undefined) {
		const aHit = score[0] >= target;
		const bHit = score[1] >= target;
		if (aHit !== bHit) return { score, outcomes: outcomesFor(aHit ? 0 : 1) };
	}
	if (score[0] !== score[1]) return { score, outcomes: outcomesFor(score[0] > score[1] ? 0 : 1) };
	return { score, outcomes: rules.allowDraw === true ? DRAW_DRAW : null };
}

/**
 * WIN / LOSS for a two-slot BOUT meet.
 *
 * Order: (1) an IRM on exactly one side → the other wins; (2) a `win_by` on
 * exactly one side → that side wins (the method is recorded on the WINNER's
 * slot, as the demo data does: "PTF", "RSC", "KO"); if the score says
 * otherwise, `conflict` is true so the screen can ask — never silently pick;
 * (3) the score, by `rules.decideBy`. `score` is rounds won (ROUNDS), judges'
 * votes (JUDGES), points (TOTAL) or the ladder figure (TALLIES).
 *
 * Source: WT Competition Rules (best of three rounds), World Boxing Technical
 * Rules (judges' decision), IJF Sport and Organisation Rules (score), FIE
 * Technical Rules (first to 5 / 15 / 45 touches).
 */
export function boutWinner(
	sides: readonly [HeadToHeadSide<BoutResultDetail>, HeadToHeadSide<BoutResultDetail>],
	rules: BoutRules,
	config?: BoutMeetConfig | null,
): BoutVerdict {
	const [a, b] = sides;
	const { score, outcomes: scoreOutcomes } = boutScore(a.detail, b.detail, rules, config);
	const byIrm = irmVerdict(a.irm, b.irm);
	if (byIrm !== undefined) return { score, outcomes: byIrm, basis: byIrm === null ? null : "IRM", scoreOutcomes, conflict: false };
	const aBy = a.detail?.win_by !== undefined;
	const bBy = b.detail?.win_by !== undefined;
	if (aBy !== bBy) {
		const outcomes = outcomesFor(aBy ? 0 : 1);
		const conflict = scoreOutcomes !== null && scoreOutcomes[0] !== outcomes[0];
		return { score, outcomes, basis: "WIN_BY", scoreOutcomes, conflict };
	}
	if (aBy && bBy) return { score, outcomes: null, basis: null, scoreOutcomes, conflict: true };
	return { score, outcomes: scoreOutcomes, basis: scoreOutcomes === null ? null : "SCORE", scoreOutcomes, conflict: false };
}

/**
 * The bout's headline from corner `side`'s point of view, as the scoreboard
 * prints it (ASCII digits): "2–1" — or, for TALLIES, the ladder figures
 * zero-padded to three digits, judo's classic "100–010".
 */
export function boutScoreLine(score: readonly [number, number], side: 0 | 1, decideBy: BoutDecision): string {
	const f = (n: number) => (decideBy === "TALLIES" ? String(n).padStart(3, "0") : String(n));
	return `${f(score[side])}–${f(score[side === 0 ? 1 : 0])}`;
}

// ─── Team ties — a parent meet decided by its rubbers ────────────────────────

/** One slot of a rubber (a child meet): whose it is and how it ended. */
export interface RubberSlot<Id> {
	competitorId: Id | null;
	outcome: MeetOutcome | null;
}

/**
 * A team tie's verdict from its rubbers (badminton's five rubbers, table
 * tennis, sepak takraw regus). `competitors` are the tie's two slots; a
 * rubber's slots are matched to them by `competitorId` (a rubber's slots hold
 * the same team competitors as the tie).
 *
 * `score` = rubbers won (WIN or BYE) — the tie's `result_value`. The first to
 * the majority of `rubbers` (default: the rubbers listed) wins, and the tie is
 * decided even if the remaining rubbers are not played. Every rubber decided
 * and level (an even number of rubbers) → DRAW. Otherwise null.
 *
 * Source: BWF Statutes, Uber / Thomas Cup regulations (best of five matches);
 * the ITTF Handbook's team match formats.
 */
export function teamTieWinner<Id>(competitors: readonly [Id, Id], rubbers: readonly (readonly RubberSlot<Id>[])[], rubbersTotal?: number): HeadToHeadVerdict {
	const won: [number, number] = [0, 0];
	let decided = 0;
	for (const rubber of rubbers) {
		let counted = false;
		for (const slot of rubber) {
			if (slot.outcome !== "WIN" && slot.outcome !== "BYE") continue;
			const side = slot.competitorId === competitors[0] ? 0 : slot.competitorId === competitors[1] ? 1 : null;
			if (side === null || counted) continue;
			won[side]++;
			counted = true;
		}
		if (counted || rubber.some((s) => s.outcome === "DRAW")) decided++;
	}
	const total = rubbersTotal ?? rubbers.length;
	const majority = Math.floor(total / 2) + 1;
	if (won[0] >= majority !== won[1] >= majority) return { score: won, outcomes: outcomesFor(won[0] >= majority ? 0 : 1) };
	if (won[0] >= majority) return { score: won, outcomes: null };
	if (total > 0 && decided === total && rubbers.length === total) {
		if (won[0] === won[1]) return { score: won, outcomes: DRAW_DRAW };
		return { score: won, outcomes: outcomesFor(won[0] > won[1] ? 0 : 1) };
	}
	return { score: won, outcomes: null };
}

// ─── ROUNDS — multi-round totals with discards ───────────────────────────────

export type ResultRound = NonNullable<RoundsResultDetail["rounds"]>[number];

export interface RoundsTotal {
	/** `result_value`: the sum of the rounds that count. null when there are no rounds. */
	total: number | null;
	/** The rounds that do not count — what each round's `discarded` flag should say. */
	discardedSeqs: number[];
}

/**
 * The total of a multi-round result (sailing series, golf, shooting series).
 *
 * With `config.discards` = n: the n WORST rounds are dropped — the highest
 * values when `lowerIsBetter` (sailing points, golf strokes), else the lowest;
 * of equal worst scores, the round sailed EARLIEST is dropped first; a round
 * marked DNE is never dropped; at least one round always counts. The stored
 * `discarded` flags are then ignored — they are an output, not an input.
 * Without `config.discards`, the stored flags are honoured (a team event
 * whose worst member score was dropped by hand).
 *
 * A round's `value` counts whatever its `irm`: a sailing DNF scores points
 * (entries + 1) and is a candidate for discard like any other.
 *
 * `factor: 2` (a medal race) doubles the round's value, in the sum and when
 * the worst rounds are picked; `keep` (the medal race) is never dropped; a
 * `decider` (jump-off, play-off) is left out altogether. With
 * `config.discard_steps` and no `discards`, the count follows the races the
 * series has counted (`discardCountOf`).
 *
 * Source: World Sailing Racing Rules of Sailing, Appendix A2.1 (excluding
 * worst scores; the earliest of equal worst first; a DNE is not excludable).
 */
export function totalOfRounds(rounds: readonly ResultRound[] | null | undefined, config: RoundsMeetConfig | null | undefined, lowerIsBetter: boolean): RoundsTotal {
	// A decider (jump-off, play-off, shoot-off) never counts: it only breaks a tie.
	const list = (rounds ?? []).filter((r) => isNumber(r.value) && r.decider !== true);
	if (list.length === 0) return { total: null, discardedSeqs: [] };
	// A medal race counts double, before discards are chosen and in the sum.
	const weighted = (r: ResultRound) => r.value * (r.factor ?? 1);
	let discarded: Set<number>;
	// Steps count the series' own races: a medal race (keep) is never one of them.
	const count = discardCountOf(config, list.filter((r) => r.keep !== true).length);
	if (count !== null) {
		const candidates = list
			.filter((r) => r.irm !== "DNE" && r.keep !== true)
			.sort((x, y) => (lowerIsBetter ? weighted(y) - weighted(x) : weighted(x) - weighted(y)) || x.seq - y.seq);
		const n = Math.max(0, Math.min(count, candidates.length, list.length - 1));
		discarded = new Set(candidates.slice(0, n).map((r) => r.seq));
	} else {
		discarded = new Set(list.filter((r) => r.discarded === true && r.keep !== true).map((r) => r.seq));
	}
	const total = roundValue(list.filter((r) => !discarded.has(r.seq)).reduce((s, r) => s + weighted(r), 0));
	return { total, discardedSeqs: [...discarded].sort((x, y) => x - y) };
}

/**
 * How many rounds a series drops: `config.discards` when set, else the step
 * of `config.discard_steps` the rounds counted have reached (sailing: from 7
 * races 1, from 11 races 2), else null — then the stored `discarded` flags
 * are honoured. Medal races and deciders are counted like any round here; the
 * caller passes the rounds that count.
 */
export function discardCountOf(config: RoundsMeetConfig | null | undefined, roundsCounted: number): number | null {
	if (config?.discards !== undefined) return config.discards;
	const steps = config?.discard_steps;
	if (!steps || steps.length === 0) return null;
	let n = 0;
	for (const s of [...steps].sort((a, b) => a.from - b.from)) if (roundsCounted >= s.from) n = s.discards;
	return n;
}
